import { getBackendUri } from "./backendUri";
export { getBackendUri } from "./backendUri";

// Debug log to see what BACKEND_URI is being used
console.debug("🔍 BACKEND_URI:", getBackendUri(), "Current location:", window.location.href);

import { ChatAppResponse, ChatAppResponseOrError, ChatAppRequest, Config, SiteId } from "./models";
import { isAuthEnabled } from "../authConfig";
import { createN8NStreamAdapter } from "./n8nAdapter";

export function getHeaders(idToken: string | undefined): Record<string, string> {
    const headers: Record<string, string> = {
        "Content-Type": "application/json"
    };
    if (isAuthEnabled && idToken) {
        headers["Authorization"] = `Bearer ${idToken}`;
    }
    return headers;
}

export async function askApi(request: ChatAppRequest, idToken: string | undefined): Promise<ChatAppResponse> {
    const response = await fetch(`${getBackendUri()}/ask`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request)
    });

    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || "Unknown error");
    }

    return parsedResponse as ChatAppResponse;
}

export async function configApi(idToken: string | undefined): Promise<Config> {
    const response = await fetch(`${getBackendUri()}/config`, {
        method: "GET",
        headers: getHeaders(idToken)
    });

    return (await response.json()) as Config;
}

export async function chatApi(request: ChatAppRequest, idToken: string | undefined, signal?: AbortSignal): Promise<Response> {
    // All chat requests go to the gateway, which selects n8n or the in-process agent.
    const response = await fetch(`${getBackendUri()}/chat`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request),
        signal
    });

    // n8n and the in-process agent backend speak the same NDJSON wire —
    // both need the stream adapter consumed by the chat UI.
    const poweredBy = response.headers.get("X-Powered-By");
    if ((poweredBy === "n8n" || poweredBy === "agent") && request.stream && response.body) {
        console.debug(`🔀 Response from ${poweredBy} — applying stream adapter`);
        const transformedStream = createN8NStreamAdapter(response.body);
        return new Response(transformedStream, {
            status: response.status,
            headers: response.headers
        });
    }

    return response;
}

// Cache for product details and ratings (2 minutes TTL)
const productDetailsCache = new Map<string, { data: { [key: string]: string }; timestamp: number }>();
const ratingCache = new Map<string, { data: { reviewCount: number; totalScore: number } | null; timestamp: number }>();

// Pending requests to prevent duplicate calls while request is in progress
const pendingProductDetails = new Map<string, Promise<{ [key: string]: string }>>();
const pendingRatings = new Map<string, Promise<{ reviewCount: number; totalScore: number } | null>>();

const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

/**
 * Fetches full product details from the internal API based on the provided SKU.
 *
 * @param sku - The SKU of the product to fetch.
 * @returns A promise that resolves to an object containing product details.
 * @throws An error if the network request fails or returns a non-OK status.
 */
export async function getProductDetails(
    sku: string,
    siteId: SiteId = "PUBLIC_GR",
    fields?: string[]
): Promise<{ [key: string]: string }> {
    // Optional field filter — request only the listed top-level fields.
    const fieldsParam = fields && fields.length ? fields.join(",") : "";
    // Include fields in the cache key so a filtered fetch never collides with a full one.
    const cacheKey = fieldsParam ? `${siteId}:${sku}:${fieldsParam}` : `${siteId}:${sku}`;
    // Check if we have cached data that's still fresh
    const cached = productDetailsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        console.log(`🎯 Using cached product details for SKU: ${sku}`);
        return cached.data;
    }

    // If already requesting this SKU, reuse the promise
    if (pendingProductDetails.has(cacheKey)) {
        console.log(`⏳ Reusing pending product details request for SKU: ${sku}`);
        return pendingProductDetails.get(cacheKey)!;
    }

    // Create new request
    const requestPromise = (async () => {
        try {
            // Encode the SKU to safely include it in the URL.
            const fieldsQuery = fieldsParam ? `&fields=${encodeURIComponent(fieldsParam)}` : "";
            const response = await fetch(`${getBackendUri()}/product_details/${encodeURIComponent(sku)}?siteId=${siteId}${fieldsQuery}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            // Check if the response status is OK (status code 200-299).
            if (!response.ok) {
                throw new Error(`Error fetching product details for SKU: ${sku}. Status: ${response.status}`);
            }

            // Parse and return the JSON response.
            const productData = await response.json();

            // Cache the result
            productDetailsCache.set(cacheKey, { data: productData, timestamp: Date.now() });

            return productData;
        } catch (error) {
            console.error("Error in getProductDetails:", error);
            throw error;
        } finally {
            // Remove from pending cache when done (success or error)
            pendingProductDetails.delete(cacheKey);
        }
    })();

    // Store the promise in pending cache
    pendingProductDetails.set(cacheKey, requestPromise);
    return requestPromise;
}

/**
 * Fetches product review scores from the internal API based on the provided SKU.
 * Uses request deduplication to prevent duplicate calls.
 *
 * @param sku - The SKU of the product to fetch ratings for.
 * @returns A promise that resolves to an object containing review data.
 * @throws An error if the network request fails or returns a non-OK status.
 */
export async function getProductRating(sku: string): Promise<{ reviewCount: number; totalScore: number } | null> {
    const cacheKey = sku;
    // Check if we have cached data that's still fresh
    const cached = ratingCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        console.debug(`🎯 Using cached rating data for SKU: ${sku}`);
        return cached.data;
    }

    // If already requesting rating for this SKU, reuse the promise
    if (pendingRatings.has(cacheKey)) {
        console.debug(`⏳ Reusing pending rating request for SKU: ${sku}`);
        return pendingRatings.get(cacheKey)!;
    }

    // Create new rating request
    const requestPromise = (async () => {
        try {
            const backendUri = getBackendUri();
            const url = `${backendUri ? backendUri : ""}/product_reviews_score/${encodeURIComponent(sku)}`;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error(`Error fetching product rating for SKU: ${sku}. Status: ${response.status}`);
            }

            const data = await response.json();
            let result = null;
            if (Array.isArray(data) && data.length > 0) {
                result = { reviewCount: data[0].review_count, totalScore: data[0].total_score };
            }

            // Cache the result
            ratingCache.set(cacheKey, { data: result, timestamp: Date.now() });

            return result;
        } catch (error) {
            console.error("Error in getProductRating:", error);
            throw error;
        } finally {
            // Remove from pending cache when done (success or error)
            pendingRatings.delete(cacheKey);
        }
    })();

    // Store the promise in pending cache
    pendingRatings.set(cacheKey, requestPromise);
    return requestPromise;
}

export function getCitationFilePath(citation: string): string {
    // Check if the citation starts with "https://www.public.gr"
    if (/^https:\/\/www\.public\.gr/.test(citation)) {
        // Create a URL object from the citation
        const url = new URL(citation);
        // Extract the last part of the pathname, which is assumed to be the SKU
        const sku = url.pathname.split("/").pop();
        // Return a new URL that uses the extracted SKU and prefixes it with your internal API route
        return `${getBackendUri()}/product_details/${encodeURIComponent(sku || "")}`;
    } else if (/^https:\/\//.test(citation)) {
        // Check if the URL contains the "|" character
        if (citation.includes("__")) {
            // Split the URL and SKU
            const [url, sku] = citation.split("__");
            // Extract the competitor's domain name from the URL
            const competitors = [new URL(url).hostname.split(".")[1]];
            // Return the formatted URL
            return `${getBackendUri()}/competitor_details/${encodeURIComponent(sku)}?competitors=${JSON.stringify(competitors)}`;
        } else {
            // Return just the URL part if it doesn't contain "|"
            return `test`;
        }
    } else {
        // For non-external URLs, prepend the backend URI and /content/
        return `${getBackendUri()}/content/${citation}`;
    }
}
